import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useLearningStore, useAuthStore } from '@shared/state';
import { trackEvent, getBaseProperties } from '@shared/services/analytics';
import { recordToneReaction } from '@shared/services/tonePreference';
import type { ToneReaction } from '@shared/utils/tone-preference';
import { ANALYTICS_EVENTS } from '@shared/constants';
import type { CheckpointData } from '@shared/types/learning-path';
import { Button } from '../ui/Button';

interface CheckpointProps {
  checkpointData: CheckpointData;
  nodeId: string;
  pathId: string;
}

export function Checkpoint({ checkpointData, nodeId, pathId }: CheckpointProps) {
  const submitCheckpoint = useLearningStore((s) => s.submitCheckpoint);
  const userId = useAuthStore((s) => s.user?.uid);
  const [selected, setSelected] = useState<string | null>(null);
  const [textAnswer, setTextAnswer] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [score, setScore] = useState<number | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [humorReacted, setHumorReacted] = useState(false);

  // Humor reaction → tone-matching loop (learns from reactions to the humor itself).
  function handleHumorReaction(reaction: ToneReaction) {
    if (humorReacted) return;
    setHumorReacted(true);
    if (userId) void recordToneReaction(userId, reaction).catch(() => {});
    trackEvent({
      name: ANALYTICS_EVENTS.CHECKPOINT_HUMOR_REACTED,
      properties: { ...getBaseProperties(), pathId, nodeId, reaction },
    }).catch(() => {});
  }

  const isMultipleChoice = checkpointData.questionType === 'multiple_choice';
  const startTime = useState(() => Date.now())[0];

  // Behavioural signal: if the learner leaves without answering, that's a skip
  // (weight-0.5 signal for the adaptive engine). Use a ref so the unmount
  // cleanup reads the latest submitted state without re-registering.
  const submittedRef = useRef(false);
  useEffect(() => {
    return () => {
      if (!submittedRef.current) {
        trackEvent({
          name: ANALYTICS_EVENTS.CHECKPOINT_SKIPPED,
          properties: {
            ...getBaseProperties(),
            pathId,
            nodeId,
            checkpointType: checkpointData.type,
          },
        }).catch(() => {});
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleSubmit() {
    const answer = isMultipleChoice ? (selected ?? '') : textAnswer;
    if (!answer) return;

    setLoading(true);
    const timeTaken = Math.round((Date.now() - startTime) / 1000);
    try {
      const result = await submitCheckpoint(nodeId, answer, timeTaken);
      setScore(result.score);
      setSubmitted(true);
      submittedRef.current = true;

      // Show feedback
      if (isMultipleChoice && checkpointData.feedback) {
        setFeedback(
          result.score >= 85
            ? checkpointData.feedback.correct
            : checkpointData.feedback.incorrect,
        );
      }

      trackEvent({
        name: ANALYTICS_EVENTS.CHECKPOINT_COMPLETED,
        properties: {
          ...getBaseProperties(),
          pathId,
          nodeId,
          checkpointType: checkpointData.type,
          score: result.score,
          timeTakenMs: timeTaken * 1000,
          attemptNumber: 1,
          signalClassification: result.signalClassification,
        },
      }).catch(() => {});
    } catch {
      setFeedback('Something went wrong. Try again.');
    } finally {
      setLoading(false);
    }
  }

  const scoreColor = score !== null
    ? score >= 80 ? 'text-success' : score >= 50 ? 'text-warning' : 'text-error'
    : '';

  return (
    <div className="rounded-lg bg-dark-surface border border-dark-border p-6">
      <p className="text-xs text-indigo-400 font-body uppercase tracking-wider mb-3 font-semibold">
        Checkpoint
      </p>

      <p className="font-body text-content mb-5">{checkpointData.question}</p>

      {/* Multiple choice options */}
      {isMultipleChoice && checkpointData.options && (
        <div className="space-y-2 mb-5">
          {checkpointData.options.map((opt, i) => {
            const isSelected = selected === String(i);
            const isCorrect = submitted && String(i) === checkpointData.correctAnswer;
            const isWrong = submitted && isSelected && !isCorrect;

            return (
              <button
                key={i}
                onClick={() => !submitted && setSelected(String(i))}
                disabled={submitted}
                className={`w-full text-left px-4 py-3 rounded text-sm font-body transition-colors cursor-pointer
                  ${isCorrect ? 'bg-success/15 border border-success text-success' : ''}
                  ${isWrong ? 'bg-error/15 border border-error text-error' : ''}
                  ${!submitted && isSelected ? 'bg-indigo-500/15 border border-indigo-500 text-content' : ''}
                  ${!submitted && !isSelected ? 'bg-dark-subtle border border-dark-border text-content-secondary hover:border-indigo-500/50' : ''}
                  ${submitted && !isCorrect && !isWrong ? 'bg-dark-subtle border border-dark-border text-content-dim opacity-60' : ''}
                `}
              >
                {opt}
              </button>
            );
          })}
        </div>
      )}

      {/* Open-ended text area */}
      {!isMultipleChoice && (
        <textarea
          value={textAnswer}
          onChange={(e) => setTextAnswer(e.target.value)}
          disabled={submitted}
          placeholder="Type your answer here..."
          rows={4}
          className="w-full px-4 py-3 rounded bg-dark-subtle border border-dark-border text-sm text-content font-body
            placeholder:text-content-dim focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500
            disabled:opacity-60 mb-5 resize-none"
        />
      )}

      {/* Submit / Score */}
      {!submitted ? (
        <Button
          onClick={handleSubmit}
          loading={loading}
          disabled={isMultipleChoice ? !selected : !textAnswer.trim()}
        >
          Check Answer
        </Button>
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-3"
        >
          {score !== null && (
            <div className="flex items-center gap-3">
              {score >= 80 && <img src="/ThumbsUpCog.png" alt="Professor Cogni" className="w-10 h-10 object-contain" />}
              <p className={`font-heading font-bold text-lg ${scoreColor}`}>
              {score >= 80 ? 'Great work!' : score >= 50 ? 'Getting there' : 'Let\'s review this'}
              {' '}
              <span className="text-sm font-body font-normal text-content-muted">({score}%)</span>
            </p>
            </div>
          )}
          {feedback && (
            <p className="text-sm text-content-secondary font-body">{feedback}</p>
          )}
          {checkpointData.hints && checkpointData.hints.length > 0 && score !== null && score < 80 && (
            <details className="text-sm">
              <summary className="text-indigo-400 cursor-pointer hover:text-indigo-300 font-body">
                Show hint
              </summary>
              <p className="mt-2 text-content-muted font-body">{checkpointData.hints[0]}</p>
            </details>
          )}

          {/* Humor reaction — tunes Cogni's vibe to you over time */}
          <div className="flex items-center gap-2 pt-1">
            {humorReacted ? (
              <span className="text-[11px] text-content-dim font-body">Thanks — tuning Cogni's vibe to you.</span>
            ) : (
              <>
                <span className="text-[11px] text-content-dim font-body mr-1">Cogni's vibe?</span>
                {([
                  { r: 'up' as const, label: '😄 More fun' },
                  { r: 'keep' as const, label: '🙂 Just right' },
                  { r: 'down' as const, label: '😐 Less' },
                ]).map(({ r, label }) => (
                  <button
                    key={r}
                    type="button"
                    onClick={() => handleHumorReaction(r)}
                    className="px-2 py-1 rounded-pill text-[11px] font-body border border-dark-border
                      text-content-muted hover:text-content hover:border-indigo-500/50 transition-colors cursor-pointer"
                  >
                    {label}
                  </button>
                ))}
              </>
            )}
          </div>
        </motion.div>
      )}
    </div>
  );
}
